/* ══════════════════════════════════════════════════════════════
   CARLA HOME — Wishlist v3
   localStorage · client-side · sin login requerido

   SISTEMAS:
   1. Core wishlist — añadir/quitar, badge contador
   2. Price drop detection — precio guardado vs actual
   3. Share wishlist via URL (?shared=handle1:variantId1,...)
   4. "You may also like" — nunca vacía, pool amplio, fallback por categoría
   5. Búsqueda personalizada — sugerencias nutridas por wishlist
   6. Cross-sell inteligente en PDP
   7. Personalización global — window.CHPersonalization
   ══════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var STORAGE_KEY    = 'ch_wishlist';       /* legacy v1 — solo lectura para migración */
  var STORAGE_KEY_V2 = 'ch_wishlist_v2';    /* v2 — fuente de verdad actual */
  var PRICE_KEY      = 'ch_wishlist_prices';
  var VIEWED_KEY     = 'ch_wishlist_viewed';

  /* Wishlist page state */
  var activeFolder           = 'all-my-wishlist';
  var _wishlistChangeHandler = null;

  /* Wishlist page UI state */
  var activeCategory = 'all';
  var selectMode     = false;
  var selectedItems  = {};   /* { itemId: true } */
  var currentSort    = 'date-added';
  var currentSearch  = '';

  /* ════════════════════════════════════
     V2 — ESTRUCTURA BASE Y PERSISTENCIA
  ════════════════════════════════════ */

  function initV2Structure() {
    return {
      folders: {
        'all-my-wishlist': {
          name: 'All My Wishlist',
          deletable: false,
          items: []
        }
      },
      version: 2
    };
  }

  function getWishlistV2() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY_V2);
      if (!raw) return initV2Structure();
      var data = JSON.parse(raw);
      if (!data || !data.folders || !data.folders['all-my-wishlist']) return initV2Structure();
      return data;
    } catch (e) {
      return initV2Structure();
    }
  }

  function saveWishlistV2(data) {
    try { localStorage.setItem(STORAGE_KEY_V2, JSON.stringify(data)); } catch (e) {}
  }

  /* ════════════════════════════════════
     MIGRACIÓN v1 → v2
  ════════════════════════════════════ */

  function migrateFromV1() {
    try {
      if (localStorage.getItem(STORAGE_KEY_V2)) return; /* v2 ya existe, no migrar */
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var v1items = JSON.parse(raw);
      if (!Array.isArray(v1items)) return;
      var data = initV2Structure();
      var now = Date.now();
      v1items.forEach(function (item) {
        data.folders['all-my-wishlist'].items.push({
          id:        String(item.id),
          variantId: null,
          title:     item.title  || '',
          url:       item.url    || '',
          image:     item.image  || '',
          price:     item.price  || '',
          category:  null,
          addedAt:   now
        });
      });
      saveWishlistV2(data);
      /* ch_wishlist legacy se conserva para posible rollback */
    } catch (e) {}
  }

  /* ════════════════════════════════════
     UTILIDADES BASE
  ════════════════════════════════════ */

  /* Compatibilidad hacia atrás — devuelve array plano de "all-my-wishlist" */
  function getWishlist() {
    return getWishlistV2().folders['all-my-wishlist'].items;
  }

  /* Compatibilidad hacia atrás — sobreescribe items de "all-my-wishlist" */
  function saveWishlist(items) {
    var data = getWishlistV2();
    data.folders['all-my-wishlist'].items = items;
    saveWishlistV2(data);
  }

  function getSavedPrices() {
    try { return JSON.parse(localStorage.getItem(PRICE_KEY) || '{}'); } catch (e) { return {}; }
  }
  function savePrices(prices) {
    try { localStorage.setItem(PRICE_KEY, JSON.stringify(prices)); } catch (e) {}
  }

  function isInWishlist(variantIdOrProductId, fallbackProductId) {
    var sid = String(variantIdOrProductId);
    var fid = fallbackProductId ? String(fallbackProductId) : null;
    return getWishlist().some(function (i) {
      /* Item con variantId: comparar por variantId */
      if (i.variantId) return i.variantId === sid;
      /* Item legacy (variantId: null): comparar por product id.
         sid puede ser un variantId distinto al product id,
         por eso se usa fid (product id) como fallback explícito. */
      return i.id === (fid || sid);
    });
  }

  function addToWishlist(product) {
    var data = getWishlistV2();
    var allItems = data.folders['all-my-wishlist'].items;

    /* Deduplicación por variantId si ambos tienen; si no, por productId */
    var exists = allItems.some(function (i) {
      if (product.variantId && i.variantId) return i.variantId === String(product.variantId);
      return i.id === String(product.id);
    });
    if (exists) return;

    var item = {
      id:           String(product.id),
      variantId:    product.variantId    ? String(product.variantId) : null,
      variantTitle: product.variantTitle || null,
      title:        product.title        || '',
      url:          product.url          || '',
      image:        product.image        || '',
      price:        product.price        || '',
      category:     product.category     || null,
      addedAt:      Date.now()
    };
    allItems.push(item);
    saveWishlistV2(data);

    var prices = getSavedPrices();
    prices[item.variantId || item.id] = item.price;
    savePrices(prices);
  }

  function removeFromWishlist(variantIdOrProductId, folderSlug, fallbackProductId) {
    var sid = String(variantIdOrProductId);
    var fid = fallbackProductId ? String(fallbackProductId) : null;
    var data = getWishlistV2();

    function matchItem(i) {
      if (i.variantId) return i.variantId === sid;
      /* Item legacy (variantId: null): usar fid si existe, si no sid */
      return i.id === (fid || sid);
    }

    if (!folderSlug || folderSlug === 'all-my-wishlist') {
      /* Eliminar de TODAS las folders */
      Object.keys(data.folders).forEach(function (key) {
        data.folders[key].items = data.folders[key].items.filter(function (i) { return !matchItem(i); });
      });
    } else {
      /* Eliminar solo de la folder específica */
      if (data.folders[folderSlug]) {
        data.folders[folderSlug].items = data.folders[folderSlug].items.filter(function (i) { return !matchItem(i); });
      }
    }
    saveWishlistV2(data);
  }

  function toggleWishlist(product) {
    var checkId = product.variantId || product.id;
    if (isInWishlist(checkId, product.id)) { removeFromWishlist(checkId, null, product.id); return false; }
    else { addToWishlist(product); return true; }
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function parsePriceCents(priceStr) {
    if (!priceStr) return 0;
    return Math.round(parseFloat(String(priceStr).replace(/[^0-9.]/g, '') || 0) * 100);
  }

  /* ════════════════════════════════════
     SLUG HELPER
  ════════════════════════════════════ */

  function slugify(name) {
    return name
      .toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60);
  }

  /* ════════════════════════════════════
     FOLDER HELPERS
  ════════════════════════════════════ */

  function getFolders() {
    return getWishlistV2().folders;
  }

  function getFolder(slug) {
    var data = getWishlistV2();
    return data.folders[slug] ? data.folders[slug].items : [];
  }

  function createFolder(name) {
    var data = getWishlistV2();
    var base = slugify(name) || 'folder';
    var slug = base;
    var n = 2;
    while (data.folders[slug]) { slug = base + '-' + n++; }
    data.folders[slug] = { name: name, deletable: true, items: [] };
    saveWishlistV2(data);
    return slug;
  }

  function renameFolder(slug, newName) {
    if (slug === 'all-my-wishlist') return false;
    var data = getWishlistV2();
    if (!data.folders[slug]) return false;
    data.folders[slug].name = newName;
    saveWishlistV2(data);
    return true;
  }

  function deleteFolder(slug) {
    if (slug === 'all-my-wishlist') return false;
    var data = getWishlistV2();
    if (!data.folders[slug]) return false;
    delete data.folders[slug];
    saveWishlistV2(data);
    return true;
  }

  function moveToFolder(variantId, fromSlug, toSlug) {
    var data = getWishlistV2();
    if (!data.folders[fromSlug] || !data.folders[toSlug]) return false;
    var sid = String(variantId);
    var fromItems = data.folders[fromSlug].items;
    var idx = -1;
    fromItems.forEach(function (i, index) {
      if (idx === -1 && ((i.variantId && i.variantId === sid) || (!i.variantId && i.id === sid))) {
        idx = index;
      }
    });
    if (idx === -1) return false;
    var item = fromItems.splice(idx, 1)[0];
    var alreadyIn = data.folders[toSlug].items.some(function (i) {
      return (i.variantId && i.variantId === sid) || (!i.variantId && i.id === sid);
    });
    if (!alreadyIn) data.folders[toSlug].items.push(item);
    saveWishlistV2(data);
    return true;
  }

  function copyToFolder(variantId, toSlug) {
    var data = getWishlistV2();
    if (!data.folders[toSlug]) return false;
    var sid = String(variantId);
    var source = null;
    data.folders['all-my-wishlist'].items.forEach(function (i) {
      if (!source && ((i.variantId && i.variantId === sid) || (!i.variantId && i.id === sid))) {
        source = i;
      }
    });
    if (!source) return false;
    var alreadyIn = data.folders[toSlug].items.some(function (i) {
      return (i.variantId && i.variantId === sid) || (!i.variantId && i.id === sid);
    });
    if (!alreadyIn) data.folders[toSlug].items.push(source);
    saveWishlistV2(data);
    return true;
  }

  function getItemsByCategory(category, folderSlug) {
    var items = folderSlug ? getFolder(folderSlug) : getWishlist();
    return items.filter(function (i) { return i.category === category; });
  }

  function searchItems(query, folderSlug) {
    var items = folderSlug ? getFolder(folderSlug) : getWishlist();
    var q = (query || '').toLowerCase().trim();
    if (!q) return items;
    return items.filter(function (i) {
      return i.title.toLowerCase().indexOf(q) !== -1;
    });
  }

  function sortItems(items, sortBy) {
    var arr = items.slice();
    switch (sortBy) {
      case 'price-low':
        arr.sort(function (a, b) {
          return parseFloat(a.price.replace(/[^0-9.]/g, '') || 0) -
                 parseFloat(b.price.replace(/[^0-9.]/g, '') || 0);
        });
        break;
      case 'price-high':
        arr.sort(function (a, b) {
          return parseFloat(b.price.replace(/[^0-9.]/g, '') || 0) -
                 parseFloat(a.price.replace(/[^0-9.]/g, '') || 0);
        });
        break;
      case 'title-az':
        arr.sort(function (a, b) { return a.title.localeCompare(b.title); });
        break;
      case 'date-added':
      default:
        arr.sort(function (a, b) { return (b.addedAt || 0) - (a.addedAt || 0); });
        break;
    }
    return arr;
  }

  function getShareUrl(folderSlug, selectedVariantIds) {
    var slug  = folderSlug || 'all-my-wishlist';
    var items = getFolder(slug);
    if (!items.length) return null;
    var toShare = (selectedVariantIds && selectedVariantIds.length)
      ? items.filter(function (i) { return selectedVariantIds.indexOf(i.variantId || i.id) !== -1; })
      : items;
    if (!toShare.length) return null;
    var params = toShare.map(function (i) {
      var handle = (i.url.split('/products/')[1] || '').split('?')[0];
      return handle + ':' + (i.variantId || i.id);
    }).join(',');
    return window.location.origin + '/pages/my-wishlist?shared=' + params;
  }

  function importFromShareUrl() {
    var params = new URLSearchParams(window.location.search);
    var shared = params.get('shared');
    if (!shared) return [];
    return shared.split(',').filter(Boolean).map(function (part) {
      var bits = part.split(':');
      return { handle: bits[0] || null, variantId: bits[1] || null };
    });
  }

  /* ════════════════════════════════════
     BOTONES WISHLIST — init y estado
  ════════════════════════════════════ */

  function updateBtn(btn, active) {
    var empty  = btn.querySelector('.ch-wl-icon--empty');
    var filled = btn.querySelector('.ch-wl-icon--filled');
    if (active) {
      btn.classList.add('ch-wl-active');
      btn.setAttribute('aria-pressed', 'true');
      if (empty)  empty.style.display = 'none';
      if (filled) filled.style.display = 'inline-flex';
    } else {
      btn.classList.remove('ch-wl-active');
      btn.setAttribute('aria-pressed', 'false');
      if (empty)  empty.style.display = 'inline-flex';
      if (filled) filled.style.display = 'none';
    }
  }

  function popBtn(btn) {
    btn.classList.remove('ch-wl-pop');
    void btn.offsetWidth;
    btn.classList.add('ch-wl-pop');
    btn.addEventListener('animationend', function () { btn.classList.remove('ch-wl-pop'); }, { once: true });
  }

  function initBtns() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
      if (btn._chWlInited) return;
      btn._chWlInited = true;
      var checkId = btn.getAttribute('data-variant-id') || btn.getAttribute('data-product-id');
      updateBtn(btn, isInWishlist(checkId));
      btn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
        var product = {
          id:           btn.getAttribute('data-product-id'),
          variantId:    btn.getAttribute('data-variant-id'),
          variantTitle: btn.getAttribute('data-variant-title'),
          title:        btn.getAttribute('data-product-title'),
          url:          btn.getAttribute('data-product-url'),
          image:        btn.getAttribute('data-product-image'),
          price:        btn.getAttribute('data-product-price'),
          category:     btn.getAttribute('data-category'),
        };
        var added = toggleWishlist(product);
        updateBtn(btn, added);
        popBtn(btn);
        var syncId = product.variantId || product.id;
        document.querySelectorAll('[data-wishlist-btn]').forEach(function (other) {
          if (other === btn) return;
          var otherId = other.getAttribute('data-variant-id') || other.getAttribute('data-product-id');
          if (otherId === syncId) updateBtn(other, added);
        });
        updateCounter();
        document.dispatchEvent(new CustomEvent('ch:wishlist:change', {
          detail: { product: product, added: added, wishlist: getWishlist() }
        }));
      });
    });
  }



  /* ════════════════════════════════════
     1. BADGE CONTADOR EN HEADER
  ════════════════════════════════════ */

  function updateCounter() {
    var count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  /* ════════════════════════════════════
     2. PRICE DROP DETECTION
  ════════════════════════════════════ */

  function checkPriceDrops(callback) {
    var list = getWishlist();
    if (!list.length) { callback({}); return; }
    var drops   = {};
    var pending = list.length;

    list.forEach(function (item) {
      var handle = item.url.split('/products/')[1];
      if (!handle) { if (--pending === 0) callback(drops); return; }
      handle = handle.split('?')[0];

      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var variant = null;
          if (item.variantId) {
            data.variants.forEach(function (v) {
              if (!variant && String(v.id) === String(item.variantId)) variant = v;
            });
          }
          var currentPrice = variant ? variant.price : data.price;
          var key          = item.variantId || item.id;
          var prices       = getSavedPrices();
          var savedPrice   = prices[key];
          if (savedPrice) {
            var savedCents = Math.round(parseFloat(savedPrice.replace(/[^0-9.]/g, '')) * 100);
            if (currentPrice < savedCents) {
              drops[key] = {
                oldPrice: savedPrice,
                newPrice: formatMoney(currentPrice),
                savings:  formatMoney(savedCents - currentPrice),
              };
            }
          }
          prices[key] = formatMoney(currentPrice);
          savePrices(prices);
        })
        .catch(function () {})
        .finally(function () { if (--pending === 0) callback(drops); });
    });
  }

  /* Badge "Price Drop" — solo en las cards de la página wishlist,
     nunca en cards del resto del sitio (no se llama desde ningún otro sitio). */
  function applyPriceDropBadges(container, drops) {
    if (!container) return;
    container.querySelectorAll('.card-wrapper').forEach(function (card) {
      var wlBtn = card.querySelector('[data-wishlist-btn]');
      if (!wlBtn) return;
      var key = wlBtn.getAttribute('data-variant-id') || wlBtn.getAttribute('data-product-id');
      if (!key || !drops[key]) return;

      var media = card.querySelector('.card__media .media');
      if (!media) return;
      var badges = media.querySelector('.ch-product-badges');

      /* No duplicar señal: si ya hay badge de oferta visible (-X%), no añadir Price Drop */
      var saleBadge = badges ? badges.querySelector('.ch-product-badge--sale') : null;
      if (saleBadge && saleBadge.style.display !== 'none') return;

      if (!badges) {
        badges = document.createElement('div');
        badges.className = 'ch-product-badges ch-product-badges--card';
        badges.setAttribute('role', 'list');
        badges.setAttribute('aria-label', 'Product highlights');
        media.insertBefore(badges, media.firstChild);
      }
      if (badges.querySelector('.ch-product-badge--price-drop')) return;

      var badge = document.createElement('span');
      badge.className = 'ch-product-badge ch-product-badge--price-drop';
      badge.setAttribute('role', 'listitem');
      badge.textContent = 'Price Drop';
      badges.appendChild(badge);
    });
  }

  /* Banner "poco stock" — cuenta badges de low-stock ya visibles en las cards
     renderizadas (nunca sobre agotados, esos ya tienen su propio "Notify me"). */
  function renderLowStockBanner() {
    var banner = document.getElementById('ch-wl-low-stock-banner');
    var container = document.getElementById('ch-wishlist-items');
    if (!banner || !container) return;
    var count = 0;
    container.querySelectorAll('.ch-product-badge--low-stock').forEach(function (badge) {
      if (badge.style.display !== 'none') count++;
    });
    if (!count) { banner.style.display = 'none'; return; }
    banner.innerHTML = '<strong>' + count + ' item' + (count > 1 ? 's' : '') + '</strong> in your wishlist ' +
      (count > 1 ? 'have' : 'has') + ' low stock — grab ' + (count > 1 ? 'them' : 'it') + ' before they sell out.';
    banner.style.display = 'flex';
  }

  /* ════════════════════════════════════
     3. SHARE WISHLIST VIA URL
  ════════════════════════════════════ */

  function loadSharedWishlist() {
    var params = new URLSearchParams(window.location.search);

    /* Nuevo formato: ?shared=handle1:variantId1,handle2:variantId2 */
    var shared = params.get('shared');
    if (shared) {
      if (getWishlist().length > 0) return;
      var sharedItems = importFromShareUrl();
      if (sharedItems.length) resolveSharedProductsByHandles(sharedItems);
      return;
    }

    /* Formato legacy: ?wl=id1,id2,id3 */
    var wlParam = params.get('wl');
    if (!wlParam) return;
    var ids = wlParam.split(',').filter(Boolean);
    if (!ids.length) return;
    if (getWishlist().length > 0) return;
    resolveSharedProducts(ids);
  }

  /* Resolver IDs legacy (formato ?wl=) */
  function resolveSharedProducts(ids) {
    fetch('/collections/all/products.json?limit=250')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var resolved = [];
        ids.forEach(function (id) {
          var found = data.products.find(function (p) { return String(p.id) === String(id); });
          if (found) {
            resolved.push({
              id:        String(found.id),
              variantId: found.variants[0] ? String(found.variants[0].id) : null,
              title:     found.title,
              url:       '/products/' + found.handle,
              image:     found.images[0] ? found.images[0].src : '',
              price:     formatMoney(found.variants[0].price * 100),
              category:  null,
              addedAt:   Date.now()
            });
          }
        });
        if (resolved.length) {
          saveWishlist(resolved);
          updateCounter();
          if (document.getElementById('ch-wishlist-items')) renderWishlistPage();
        }
      })
      .catch(function () {});
  }

  /* Resolver handles+variantIds del nuevo formato (?shared=) */
  function resolveSharedProductsByHandles(sharedItems) {
    var pending = sharedItems.length;
    if (!pending) return;
    sharedItems.forEach(function (shared) {
      if (!shared.handle) {
        if (--pending === 0) { updateCounter(); if (document.getElementById('ch-wishlist-items')) renderWishlistPage(); }
        return;
      }
      fetch('/products/' + shared.handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var variant = null;
          if (shared.variantId) {
            data.variants.forEach(function (v) {
              if (!variant && String(v.id) === String(shared.variantId)) variant = v;
            });
          }
          if (!variant) variant = data.variants[0];
          addToWishlist({
            id:        String(data.id),
            variantId: variant ? String(variant.id) : null,
            title:     data.title,
            url:       '/products/' + data.handle,
            image:     data.images[0] ? data.images[0].src : '',
            price:     formatMoney(variant ? variant.price : data.variants[0].price),
            category:  null,
            addedAt:   Date.now()
          });
        })
        .catch(function () {})
        .finally(function () {
          if (--pending === 0) {
            updateCounter();
            if (document.getElementById('ch-wishlist-items')) renderWishlistPage();
          }
        });
    });
  }

  /* ════════════════════════════════════
     4. "YOU MAY ALSO LIKE" — NUNCA VACÍA

     Estrategia multi-seed con fallback:
     - Pide pool de 12 por cada seed de la wishlist
     - Filtra ya-en-wishlist
     - Si queda < 4, usa otro seed (siguiente producto wishlist)
     - Si Shopify devuelve 0, fallback por categoría del primer item
     - Nunca oculta el bloque si hay wishlist items
  ════════════════════════════════════ */

  function renderRecommendations() {
  return;
    var container = document.getElementById('ch-wl-recommendations');
    if (!container) return;

    var list = getWishlist();

    /* Si wishlist vacía → usar recomendaciones genéricas de colección */
    if (!list.length) {
      fetchFallbackRecs([], function (recs) {
        if (recs.length) { showRecs(container, recs); }
        else { container.style.display = 'none'; }
      });
      return;
    }

    /* Recopilar seeds: todos los IDs de la wishlist (se excluyen entradas sin id valido) */
    var seeds     = list.map(function (i) { return i.id; }).filter(function (id) { return !!id; });
    var collected = [];
    var seedIndex = 0;
    var wishlistIds = list.map(function (i) { return String(i.id); });

    function tryNextSeed() {
      if (seedIndex >= seeds.length) {
        /* Agotamos todos los seeds — si aún no hay suficientes, fallback por categoría */
        if (collected.length < 2) {
          fetchFallbackRecs(wishlistIds, function (fallback) {
            var combined = collected.concat(fallback)
              .filter(function (p, i, arr) {
                return arr.findIndex(function (x) { return String(x.id) === String(p.id); }) === i;
              })
              .slice(0, 8);
            if (combined.length) showRecs(container, combined);
            else container.style.display = 'none';
          });
        } else {
          showRecs(container, collected.slice(0, 8));
        }
        return;
      }

      fetch('/recommendations/products.json?product_id=' + seeds[seedIndex] + '&limit=12&intent=related')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.products && data.products.length) {
            data.products.forEach(function (p) {
              var alreadyIn = wishlistIds.indexOf(String(p.id)) !== -1;
              var alreadyCollected = collected.some(function (c) { return String(c.id) === String(p.id); });
              if (!alreadyIn && !alreadyCollected) {
                collected.push(p);
              }
            });
          }
          seedIndex++;
          /* Si ya tenemos 8, parar */
          if (collected.length >= 8) {
            showRecs(container, collected.slice(0, 8));
          } else {
            tryNextSeed();
          }
        })
        .catch(function () {
          seedIndex++;
          tryNextSeed();
        });
    }

    tryNextSeed();
  }

  /* Fallback: coger productos de /collections/all filtrando los ya en wishlist */
  function fetchFallbackRecs(excludeIds, callback) {
    fetch('/collections/all/products.json?limit=12&sort_by=best-selling')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var filtered = (data.products || []).filter(function (p) {
          return excludeIds.indexOf(String(p.id)) === -1;
        }).slice(0, 8);
        /* Adaptar formato al de /recommendations/products.json */
        var adapted = filtered.map(function (p) {
          return {
            id:              p.id,
            title:           p.title,
            url:             '/products/' + p.handle,
            featured_image:  p.images && p.images[0] ? p.images[0].src : null,
            price:           p.variants[0].price,
            variants:        p.variants,
          };
        });
        callback(adapted);
      })
      .catch(function () { callback([]); });
  }

  function buildRecCard(p) {
    var price = formatMoney(p.price);
    /* featured_image puede ser URL completa o relativa */
    var imgSrc = p.featured_image || '';
    if (imgSrc && imgSrc.indexOf('http') !== 0 && imgSrc.indexOf('//') !== 0) {
      imgSrc = 'https:' + imgSrc;
    }
    var card = document.createElement('div');
    card.className = 'ch-wl-card';
    card.innerHTML =
      '<a href="' + p.url + '" class="ch-wl-card-img-link" tabindex="-1" aria-hidden="true">' +
        (imgSrc
          ? '<img src="' + escHtml(imgSrc) + '" alt="' + escHtml(p.title) + '" loading="lazy">'
          : '<div class="ch-wl-card-img-ph"></div>') +
      '</a>' +
      '<div class="ch-wl-card-info">' +
        '<a href="' + p.url + '" class="ch-wl-card-title">' + escHtml(p.title) + '</a>' +
        '<div class="ch-wl-card-price">' + price + '</div>' +
      '</div>' +
      '<div class="ch-wl-card-btns">' +
        /* Wishlist — ch-wl-sq */
        '<button class="ch-wl-sq" data-wishlist-btn' +
          ' data-product-id="' + p.id + '"' +
          ' data-product-title="' + escHtml(p.title) + '"' +
          ' data-product-url="' + p.url + '"' +
          ' data-product-image="' + escHtml(imgSrc) + '"' +
          ' data-product-price="' + price + '"' +
          ' aria-label="Save ' + escHtml(p.title) + ' to wishlist"' +
          ' aria-pressed="false" type="button">' +
          '<span class="ch-wl-icon ch-wl-icon--empty" style="display:inline-flex">' +
            '<svg width="15" height="15" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M50 82 L14 46 L14 28 L28 14 L42 14 L50 22 L58 14 L72 14 L86 28 L86 46 Z" stroke="currentColor" stroke-width="7" stroke-linejoin="round" fill="none"/></svg>' +
          '</span>' +
          '<span class="ch-wl-icon ch-wl-icon--filled" style="display:none">' +
            '<svg width="15" height="15" viewBox="0 0 100 100" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M50 82 L14 46 L14 28 L28 14 L42 14 L50 22 L58 14 L72 14 L86 28 L86 46 Z" stroke="currentColor" stroke-width="7" stroke-linejoin="round"/></svg>' +
          '</span>' +
        '</button>' +
        /* View product — ch-card-atc */
        '<a href="' + p.url + '" class="ch-card-atc">' +
          '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>' +
          'View product' +
        '</a>' +
      '</div>';
    return card;
  }

  function showRecs(container, recs) {
    container.style.display = 'block';
    var grid = container.querySelector('.ch-rec-grid');
    if (!grid) return;
    grid.innerHTML = '';
    /* Card estandar de la web (card-product) via Section Rendering API, igual que
       los productos guardados; buildRecCard solo si falla la peticion. */
    Promise.all(recs.map(function (p) {
      var handle = (p.url || '').replace(/.*\/products\//, '').split('?')[0].split('#')[0];
      if (!handle) return Promise.resolve({ p: p, html: null });
      return fetch('/products/' + handle + '?sections=wishlist-card-renderer', {
        headers: { 'X-Requested-With': 'fetch' }
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (json) { return { p: p, html: (json && json['wishlist-card-renderer']) || null }; })
        .catch(function () { return { p: p, html: null }; });
    })).then(function (results) {
      results.forEach(function (result) {
        var tmp = document.createElement('div');
        tmp.innerHTML = (result.html || '').trim();
        var card = tmp.querySelector('.card-wrapper');
        grid.appendChild(card || buildRecCard(result.p));
      });
      initBtns();
      if (window.chBisApplySubscribedState) window.chBisApplySubscribedState(grid);
    });
  }

  /* ════════════════════════════════════
     5. BÚSQUEDA PERSONALIZADA
     Banner de wishlist en resultados de búsqueda
  ════════════════════════════════════ */

  function personalizeSearch() {
    var searchInput = document.querySelector('.search__input, input[name="q"]');
    if (!searchInput) return;
    var query = searchInput.value.trim().toLowerCase();
    if (!query || query.length < 2) return;

    var list = getWishlist();
    if (!list.length) return;

    var matches = list.filter(function (item) {
      return item.title.toLowerCase().includes(query) ||
             item.url.toLowerCase().includes(query.replace(/\s+/g, '-'));
    });
    if (!matches.length) return;

    var grid = document.getElementById('product-grid');
    if (!grid) return;

    var existing = document.getElementById('ch-search-wl-banner');
    if (existing) existing.remove();

    var banner = document.createElement('div');
    banner.id = 'ch-search-wl-banner';
    banner.style.cssText = [
      'grid-column: 1 / -1',
      'background: rgba(193,123,94,0.08)',
      'border: 1px solid rgba(193,123,94,0.2)',
      'border-radius: 4px',
      'padding: 12px 16px',
      'margin-bottom: 4px',
      'display: flex',
      'align-items: center',
      'gap: 10px',
      'font-family: var(--ch-font-ui)',
      'font-size: 1.3rem',
      'color: #2C2825',
    ].join(';');
    banner.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 100 100" fill="#C17B5E" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M50 82 L14 46 L14 28 L28 14 L42 14 L50 22 L58 14 L72 14 L86 28 L86 46 Z" stroke="#C17B5E" stroke-width="7" stroke-linejoin="round"/>' +
      '</svg>' +
      '<span>You have <strong>' + matches.length + ' saved item' + (matches.length > 1 ? 's' : '') +
      '</strong> matching this search — <a href="/pages/my-wishlist" style="color:#C17B5E;text-decoration:underline;">view your wishlist</a></span>';
    grid.insertBefore(banner, grid.firstChild);
  }

  /* ════════════════════════════════════
     6. CROSS-SELL EN PDP
  ════════════════════════════════════ */

  function pdpCrossSell() {
    if (!document.body.classList.contains('template-product') &&
        !window.location.pathname.includes('/products/')) return;

    var list = getWishlist();
    if (!list.length) return;

    var pdpEl = document.querySelector('[data-category-n1]');
    var category = pdpEl ? pdpEl.getAttribute('data-category-n1') : '';
    if (!category) return;

    var similar = list.filter(function (item) {
      return item.url.includes(category) || item.title.toLowerCase().includes(category);
    });
    if (!similar.length) return;

    var buyBtn = document.querySelector('.product-form__submit, [name="add"]');
    if (!buyBtn) return;
    var insertAfter = buyBtn.closest('form') || buyBtn.parentElement;
    if (document.getElementById('ch-pdp-crossell')) return;

    var banner = document.createElement('div');
    banner.id = 'ch-pdp-crossell';
    banner.style.cssText = [
      'margin-top: 12px', 'padding: 10px 14px',
      'background: rgba(193,123,94,0.07)',
      'border-left: 2px solid #C17B5E',
      'border-radius: 0 4px 4px 0',
      'font-family: var(--ch-font-ui)',
      'font-size: 1.3rem', 'color: #2C2825', 'line-height: 1.5',
    ].join(';');
    var item = similar[0];
    banner.innerHTML =
      '<svg width="13" height="13" viewBox="0 0 100 100" fill="#C17B5E" xmlns="http://www.w3.org/2000/svg" style="vertical-align:middle;margin-right:6px">' +
        '<path d="M50 82 L14 46 L14 28 L28 14 L42 14 L50 22 L58 14 L72 14 L86 28 L86 46 Z" stroke="#C17B5E" stroke-width="7" stroke-linejoin="round"/>' +
      '</svg>' +
      'You saved <a href="' + item.url + '" style="color:#C17B5E;text-decoration:underline;">' +
      escHtml(item.title.length > 40 ? item.title.substring(0, 40) + '…' : item.title) +
      '</a> — a similar item';
    insertAfter.insertAdjacentElement('afterend', banner);
  }

  /* ════════════════════════════════════
     7. PERSONALIZACIÓN GLOBAL
     window.CHPersonalization — datos disponibles
     para cualquier componente en la página
  ════════════════════════════════════ */

  function loadPersonalization() {
    var wishlist = getWishlist();
    var wishlistIds = wishlist.map(function (i) { return String(i.id); });

    /* Categorías más guardadas en wishlist */
    var catCounts = {};
    wishlist.forEach(function (item) {
      var parts = item.url.split('/');
      var cat   = parts[parts.length - 1].split('-')[0];
      catCounts[cat] = (catCounts[cat] || 0) + 1;
    });
    var topCategory = Object.keys(catCounts).sort(function (a, b) {
      return catCounts[b] - catCounts[a];
    })[0] || null;

    /* Cargar datos del carrito */
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var cartIds = (cart.items || []).map(function (i) { return String(i.product_id); });
        window.CHPersonalization = {
          wishlist:    wishlist,
          wishlistIds: wishlistIds,
          cartIds:     cartIds,
          topCategory: topCategory,
          hasData:     wishlist.length > 0 || cartIds.length > 0,
        };
        document.dispatchEvent(new CustomEvent('ch:personalization:ready', {
          detail: window.CHPersonalization
        }));
      })
      .catch(function () {
        window.CHPersonalization = {
          wishlist:    wishlist,
          wishlistIds: wishlistIds,
          cartIds:     [],
          topCategory: topCategory,
          hasData:     wishlist.length > 0,
        };
      });
  }

  /* ════════════════════════════════════
     WISHLIST PAGE
  ════════════════════════════════════ */

  function updatePageCountDisplay(count) {
    var el = document.getElementById('ch-wl-page-count');
    if (el) el.textContent = count ? count + ' item' + (count > 1 ? 's' : '') : '';
  }

  function buildFallbackCard(item) {
    var card = document.createElement('div');
    card.className = 'ch-wl-card';
    card.innerHTML =
      '<a href="' + item.url + '" class="ch-wl-card-img-link" tabindex="-1" aria-hidden="true">' +
        (item.image ? '<img src="' + item.image + '" alt="' + escHtml(item.title) + '" loading="lazy">' : '') +
      '<\/a>' +
      '<div class="ch-wl-card-remove-overlay">' +
        '<button class="ch-wl-card-remove ch-wl-sq ch-wl-active" data-wishlist-btn' +
          ' data-product-id="' + item.id + '"' +
          ' data-variant-id="' + (item.variantId || '') + '"' +
          ' data-product-title="' + escHtml(item.title) + '"' +
          ' data-product-url="' + item.url + '"' +
          ' data-product-image="' + (item.image || '') + '"' +
          ' data-product-price="' + item.price + '"' +
          ' data-category="' + (item.category || '') + '"' +
          ' aria-label="Remove ' + escHtml(item.title) + ' from wishlist"' +
          ' aria-pressed="true" type="button">' +
          '<span class="ch-wl-icon ch-wl-icon--empty" style="display:none"><svg width="15" height="15" viewBox="0 0 100 100" fill="none"><path d="M50 82 L14 46 L14 28 L28 14 L42 14 L50 22 L58 14 L72 14 L86 28 L86 46 Z" stroke="currentColor" stroke-width="7" stroke-linejoin="round" fill="none"><\/path><\/svg><\/span>' +
          '<span class="ch-wl-icon ch-wl-icon--filled" style="display:inline-flex"><svg width="15" height="15" viewBox="0 0 100 100" fill="currentColor"><path d="M50 82 L14 46 L14 28 L28 14 L42 14 L50 22 L58 14 L72 14 L86 28 L86 46 Z" stroke="currentColor" stroke-width="7" stroke-linejoin="round"><\/path><\/svg><\/span>' +
        '<\/button>' +
      '<\/div>' +
      '<div class="ch-wl-card-info">' +
        '<a href="' + item.url + '" class="ch-wl-card-title">' + escHtml(item.title) + '<\/a>' +
        '<div class="ch-wl-card-price">' + item.price + '<\/div>' +
      '<\/div>' +
      '<div class="ch-wl-card-btns">' +
        '<a href="' + item.url + '" class="ch-card-atc">View product<\/a>' +
      '<\/div>';
    return card;
  }

  function renderFolderSidebar() {
    var list = document.getElementById('ch-wl-folder-list');
    if (!list) return;
    var folders = getFolders();
    var svgFolder = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"\/><\/svg>';
    var svgRename = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"\/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"\/><\/svg>';
    var svgTrash  = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"\/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"\/><\/svg>';
    var html = '';
    Object.keys(folders).forEach(function (slug) {
      var folder   = folders[slug];
      var isActive = slug === activeFolder;
      var count    = folder.items ? folder.items.length : 0;
      html +=
        '<li class="ch-wl-folder-item' + (isActive ? ' ch-wl-folder-item--active' : '') + '" data-slug="' + slug + '">' +
          '<button class="ch-wl-folder-btn" data-slug="' + slug + '" type="button">' +
            svgFolder +
            '<span class="ch-wl-folder-name">' + escHtml(folder.name) + '<\/span>' +
            '<span class="ch-wl-folder-count">' + count + '<\/span>' +
          '<\/button>';
      if (folder.deletable) {
        html +=
          '<div class="ch-wl-folder-actions">' +
            '<button class="ch-wl-folder-action ch-wl-folder-rename-btn" data-slug="' + slug + '" type="button" title="Rename">' + svgRename + '<\/button>' +
            '<button class="ch-wl-folder-action ch-wl-folder-delete-btn" data-slug="' + slug + '" type="button" title="Delete">' + svgTrash + '<\/button>' +
          '<\/div>';
      }
      html += '<\/li>';
    });
    list.innerHTML = html;
  }

  function renderFolderDropdown() {
    var dropList  = document.getElementById('ch-wl-folder-drop-list');
    var dropLabel = document.getElementById('ch-wl-folder-drop-label');
    if (!dropList) return;
    var folders    = getFolders();
    var activeName = folders[activeFolder] ? folders[activeFolder].name : 'All My Wishlist';
    if (dropLabel) dropLabel.textContent = activeName;
    var html = '';
    Object.keys(folders).forEach(function (slug) {
      var folder   = folders[slug];
      var isActive = slug === activeFolder;
      var count    = folder.items ? folder.items.length : 0;
      html +=
        '<li class="ch-wl-folder-drop-item' + (isActive ? ' ch-wl-folder-drop-item--active' : '') + '">' +
          '<button class="ch-wl-folder-drop-btn" data-slug="' + slug + '" type="button">' +
            escHtml(folder.name) +
            ' <span class="ch-wl-folder-drop-count">(' + count + ')<\/span>' +
          '<\/button>' +
        '<\/li>';
    });
    dropList.innerHTML = html;
  }

  function renderWishlistPage() {
    var container = document.getElementById('ch-wishlist-items');
    var empty     = document.getElementById('ch-wishlist-empty');
    if (!container) return;

    var list = getFolder(activeFolder).slice(0, 50);
    updatePageCountDisplay(list.length);
    renderFolderSidebar();
    renderFolderDropdown();

    if (!list.length) {
      container.style.display = 'none';
      if (empty) empty.style.display = 'block';
      var lowStockBanner = document.getElementById('ch-wl-low-stock-banner');
      if (lowStockBanner) lowStockBanner.style.display = 'none';
      renderRecommendations();
      return;
    }

    container.style.display = 'grid';
    if (empty) empty.style.display = 'none';

    /* Show skeletons immediately */
    container.innerHTML = list.map(function () {
      return '<div class="ch-wl-skeleton"></div>';
    }).join('');

    /* Parallel fetch via Section Rendering API */
    Promise.all(list.map(function (item) {
      var handle = item.url
        ? item.url.replace(/.*\/products\//, '').split('?')[0].split('#')[0]
        : null;
      if (!handle) return Promise.resolve({ item: item, html: null });
      var variantParam = item.variantId ? '&variant=' + item.variantId : '';
      return fetch('/products/' + handle + '?sections=wishlist-card-renderer' + variantParam, {
        headers: { 'X-Requested-With': 'fetch' }
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (json) {
          return { item: item, html: (json && json['wishlist-card-renderer']) || null };
        })
        .catch(function () { return { item: item, html: null }; });
    })).then(function (results) {
      container.innerHTML = '';
      results.forEach(function (result) {
        if (result.html) {
          var tmp = document.createElement('div');
          tmp.innerHTML = result.html.trim();
          var card = tmp.querySelector('.card-wrapper');
          if (card) {
            card.dataset.cat        = (result.item.category || '').toLowerCase().trim();
            card.dataset.addedAt    = result.item.addedAt || 0;
            card.dataset.priceValue = parsePriceCents(result.item.price);
            card.dataset.titleValue = result.item.title || '';
            container.appendChild(card);
          }
        } else {
          var fb = buildFallbackCard(result.item);
          fb.dataset.cat        = (result.item.category || '').toLowerCase().trim();
          fb.dataset.addedAt    = result.item.addedAt || 0;
          fb.dataset.priceValue = parsePriceCents(result.item.price);
          fb.dataset.titleValue = result.item.title || '';
          container.appendChild(fb);
        }
      });
      selectedItems = {};
      updateGroupActionBtns();
      initBtns();
      updateTabCounts();
      filterCards();
      if (currentSort !== 'date-added') applySort(currentSort);
      if (selectMode) injectCheckboxes(container);
      renderShareBtn();
      renderRecommendations();
      checkPriceDrops(function (drops) { applyPriceDropBadges(container, drops); });
      if (window.chBisApplySubscribedState) window.chBisApplySubscribedState(container);
      renderLowStockBanner();
    });

    /* Re-render on item removal — replace previous handler to avoid stacking */
    if (_wishlistChangeHandler) {
      document.removeEventListener('ch:wishlist:change', _wishlistChangeHandler);
    }
    _wishlistChangeHandler = function (e) {
      document.removeEventListener('ch:wishlist:change', _wishlistChangeHandler);
      _wishlistChangeHandler = null;
      setTimeout(function () { renderWishlistPage(); updateCounter(); }, 380);
    };
    document.addEventListener('ch:wishlist:change', _wishlistChangeHandler);
  }

  /* ════════════════════════════════════
     CARD VARIANT THUMBNAILS — wishlist page
     card-product.liquid installs these listeners via its inline <script>,
     but innerHTML does not execute scripts. Run them here instead.
  ════════════════════════════════════ */
  function initCardVariants() {
    if (window.__chCardVariantsInit) return;
    window.__chCardVariantsInit = true;

    /* Prefetch images on card hover */
    document.addEventListener('mouseenter', function (e) {
      var card = e.target.closest('.card-wrapper, .card');
      if (!card || card._chPreloaded) return;
      var thumbs = card.querySelectorAll('.ch-card-variants__thumb');
      if (!thumbs.length) return;
      card._chPreloaded = true;
      thumbs.forEach(function (thumb) {
        if (thumb.dataset.variantImage) {
          var link = document.createElement('link');
          link.rel  = 'prefetch';
          link.as   = 'image';
          link.href = thumb.dataset.variantImage;
          document.head.appendChild(link);
        }
      });
    }, true);

    /* Thumbnail click — swap image, price, wishlist state */
    document.addEventListener('click', function (e) {
      if (!e.target || typeof e.target.closest !== 'function') return;
      var thumb = e.target.closest('.ch-card-variants__thumb');
      if (!thumb) return;
      e.preventDefault();

      var card = thumb.closest('.card-wrapper') || thumb.closest('.card');
      if (!card) return;

      /* Active state */
      thumb.closest('.ch-card-variants').querySelectorAll('.ch-card-variants__thumb').forEach(function (t) {
        t.classList.remove('active');
      });
      thumb.classList.add('active');

      /* Main image */
      var mainImg = card.querySelector('.card__media img.motion-reduce, .card__media img:first-child, .media img');
      if (mainImg && thumb.dataset.variantImage) {
        var newImg = new Image();
        newImg.onload = function () {
          mainImg.src    = thumb.dataset.variantImage;
          mainImg.srcset = '';
          mainImg.style.opacity = '1';
        };
        mainImg.style.opacity    = '0.5';
        mainImg.style.transition = 'opacity 0.15s ease';
        newImg.src = thumb.dataset.variantImage;
        if (newImg.complete) {
          mainImg.src    = thumb.dataset.variantImage;
          mainImg.srcset = '';
          mainImg.style.opacity = '1';
        }
      }

      /* Variant ID in ATC form */
      var input = card.querySelector('input[name="id"]');
      if (input) input.value = thumb.dataset.variantId;

      /* Price */
      var priceContainer = card.querySelector('.price');
      if (priceContainer && thumb.dataset.variantPrice) {
        var price   = parseInt(thumb.dataset.variantPrice, 10);
        var compare = parseInt(thumb.dataset.variantCompare, 10) || 0;
        var fmt = function (cents) { return '$' + (cents / 100).toFixed(2); };
        if (compare && compare > price) {
          var salEl = priceContainer.querySelector('.price-item--sale');
          var cmpEl = priceContainer.querySelector('.price__sale s.price-item--regular');
          if (salEl) salEl.textContent = fmt(price);
          if (cmpEl) cmpEl.textContent = fmt(compare);
          priceContainer.classList.remove('price--no-compare');
          priceContainer.classList.add('price--on-sale');
        } else {
          var regEl = priceContainer.querySelector('.price-item--regular');
          if (regEl) regEl.textContent = fmt(price);
          priceContainer.classList.add('price--no-compare');
          priceContainer.classList.remove('price--on-sale');
        }
      }

      /* Sync wishlist button with selected variant */
      var wlBtn = card.querySelector('.ch-wl-sq[data-wishlist-btn]');
      if (wlBtn && window.CHWishlist) {
        var vPriceFmt = thumb.dataset.variantPrice
          ? '$' + (parseInt(thumb.dataset.variantPrice, 10) / 100).toFixed(2).replace(/\.00$/, '')
          : wlBtn.dataset.productPrice;
        wlBtn.dataset.variantId    = thumb.dataset.variantId    || '';
        wlBtn.dataset.productImage = thumb.dataset.variantImage || wlBtn.dataset.productImage;
        wlBtn.dataset.productPrice = vPriceFmt;
        wlBtn.dataset.variantTitle = thumb.dataset.variantTitle || '';
        var newCheckId = thumb.dataset.variantId || wlBtn.dataset.productId;
        var isIn       = window.CHWishlist.has(newCheckId, wlBtn.dataset.productId);
        var iconEmpty  = wlBtn.querySelector('.ch-wl-icon--empty');
        var iconFilled = wlBtn.querySelector('.ch-wl-icon--filled');
        if (isIn) {
          wlBtn.classList.add('ch-wl-active');    wlBtn.setAttribute('aria-pressed', 'true');
          if (iconEmpty)  iconEmpty.style.display  = 'none';
          if (iconFilled) iconFilled.style.display = 'inline-flex';
        } else {
          wlBtn.classList.remove('ch-wl-active'); wlBtn.setAttribute('aria-pressed', 'false');
          if (iconEmpty)  iconEmpty.style.display  = 'inline-flex';
          if (iconFilled) iconFilled.style.display = 'none';
        }
        wlBtn._chWlInited = false;
        window.CHWishlist.initBtns();
      }
    });
  }

  /* ════════════════════════════════════
     WISHLIST PAGE — FILTER / SORT / SEARCH
  ════════════════════════════════════ */

  /* FIX 5: highlight matching text in visible card titles */
  function highlightSearchText(cards, query) {
    var q = query.trim();
    cards.forEach(function (card) {
      var allTitleEls = card.querySelectorAll('.card__heading a');
      var titleEl = null;
      allTitleEls.forEach(function (el) {
        if (el.offsetWidth > 0) titleEl = el;
      });
      if (!titleEl) titleEl = card.querySelector('.card__information .card__heading a');
      if (!titleEl) return;
      /* Restore original text first */
      if (titleEl._chOrigText !== undefined) {
        titleEl.textContent = titleEl._chOrigText;
        titleEl._chOrigText = undefined;
      }
      if (!q) return;
      var text = titleEl.textContent;
      var idx = text.toLowerCase().indexOf(q.toLowerCase());
      if (idx === -1) return;
      titleEl._chOrigText = text;
      titleEl.innerHTML =
        '<span style="color:var(--ch-stone);font-weight:400">' + escHtml(text.slice(0, idx)) + '</span>' +
        '<mark class="ch-wl-highlight">' + escHtml(text.slice(idx, idx + q.length)) + '</mark>' +
        '<span style="color:var(--ch-stone);font-weight:400">' + escHtml(text.slice(idx + q.length)) + '</span>';
    });
  }

  function filterCards() {
    var grid = document.getElementById('ch-wishlist-items');
    if (!grid) return;
    var q = currentSearch.toLowerCase().trim();
    var cards = Array.from(grid.querySelectorAll('.card-wrapper'));
    var visibleCards = [];
    var visibleCount = 0;
    cards.forEach(function (card) {
      var matchesCat = activeCategory === 'all' || (card.dataset.cat || '') === activeCategory;
      var matchesSearch = true;
      if (q) {
        var titleEl = card.querySelector('.card__heading a');
        var title = titleEl ? titleEl.textContent.toLowerCase() : (card.dataset.titleValue || '').toLowerCase();
        matchesSearch = title.indexOf(q) !== -1;
      }
      var show = matchesCat && matchesSearch;
      card.style.display = show ? '' : 'none';
      if (show) { visibleCards.push(card); visibleCount++; }
    });
    /* Highlight */
    highlightSearchText(visibleCards, currentSearch);
    /* Clear highlight on hidden cards */
    cards.forEach(function (card) {
      if (card.style.display === 'none') {
        var t = card.querySelector('.card__heading a');
        if (t && t._chOrigText !== undefined) { t.textContent = t._chOrigText; t._chOrigText = undefined; }
      }
    });
    var noResults = document.getElementById('ch-wl-no-results');
    if (noResults) {
      var hasFilter = q || activeCategory !== 'all';
      noResults.style.display = (visibleCount === 0 && hasFilter) ? 'block' : 'none';
      if (visibleCount === 0 && q) {
        noResults.innerHTML = 'No items found for <strong>\u201c' + escHtml(currentSearch) + '\u201d</strong>';
      } else if (visibleCount === 0) {
        noResults.innerHTML = 'No items in this category yet.';
      }
    }
    /* FIX 2: keep Select All state current after filter changes visible set */
    updateSelectAllBtn();
  }

  function applySort(sortBy) {
    currentSort = sortBy;
    var grid = document.getElementById('ch-wishlist-items');
    if (!grid) return;
    var cards = Array.from(grid.querySelectorAll('.card-wrapper'));
    if (cards.length < 2) return;
    cards.sort(function (a, b) {
      switch (sortBy) {
        case 'price-low':
          return (parseFloat(a.dataset.priceValue) || 0) - (parseFloat(b.dataset.priceValue) || 0);
        case 'price-high':
          return (parseFloat(b.dataset.priceValue) || 0) - (parseFloat(a.dataset.priceValue) || 0);
        case 'title-az':
          return (a.dataset.titleValue || '').localeCompare(b.dataset.titleValue || '');
        default:
          return (parseFloat(b.dataset.addedAt) || 0) - (parseFloat(a.dataset.addedAt) || 0);
      }
    });
    cards.forEach(function (card) { grid.appendChild(card); });
    filterCards();
  }

  function updateTabCounts() {
    var tabs = document.getElementById('ch-wl-tabs');
    if (!tabs) return;
    var items = getFolder(activeFolder);
    var catCounts = {};
    items.forEach(function (item) {
      var cat = (item.category || '').toLowerCase().trim();
      if (cat) catCounts[cat] = (catCounts[cat] || 0) + 1;
    });
    tabs.querySelectorAll('.ch-wl-tab[data-cat]').forEach(function (tab) {
      var cat = tab.dataset.cat;
      if (cat === 'all') { tab.classList.remove('ch-wl-tab--empty'); return; }
      if (catCounts[cat]) tab.classList.remove('ch-wl-tab--empty');
      else tab.classList.add('ch-wl-tab--empty');
    });
  }

  function initTabs() {
    var tabs = document.getElementById('ch-wl-tabs');
    if (!tabs || tabs._chInited) return;
    tabs._chInited = true;
    tabs.addEventListener('click', function (e) {
      var tab = e.target.closest('.ch-wl-tab');
      if (!tab) return;
      var cat = tab.dataset.cat;
      if (!cat) return;
      activeCategory = cat;
      tabs.querySelectorAll('.ch-wl-tab').forEach(function (t) {
        t.classList.remove('ch-wl-tab--active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('ch-wl-tab--active');
      tab.setAttribute('aria-selected', 'true');
      filterCards();
    });
  }

  function initSearch() {
    document.querySelectorAll('.ch-wl-search__input').forEach(function (input) {
      if (input._chInited) return;
      input._chInited = true;
      var clearBtn = input.parentElement ? input.parentElement.querySelector('.ch-wl-search__clear') : null;
      var timer = null;
      input.addEventListener('input', function () {
        currentSearch = input.value;
        /* Sync sibling inputs */
        document.querySelectorAll('.ch-wl-search__input').forEach(function (other) {
          if (other !== input) other.value = currentSearch;
        });
        /* Sync sibling clear buttons */
        document.querySelectorAll('.ch-wl-search__clear').forEach(function (btn) {
          btn.style.display = currentSearch ? 'flex' : 'none';
        });
        clearTimeout(timer);
        timer = setTimeout(filterCards, 200);
      });
      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          currentSearch = '';
          document.querySelectorAll('.ch-wl-search__input').forEach(function (inp) { inp.value = ''; });
          document.querySelectorAll('.ch-wl-search__clear').forEach(function (btn) { btn.style.display = 'none'; });
          filterCards();
        });
      }
    });
  }

  function syncSortUI(sortVal) {
    var desktopSel = document.getElementById('ch-wl-sort-select');
    if (desktopSel) desktopSel.value = sortVal;
    document.querySelectorAll('.ch-wl-bs-sort-opt').forEach(function (opt) {
      var isActive = opt.dataset.sort === sortVal;
      opt.classList.toggle('ch-wl-bs-sort-opt--active', isActive);
      var check = opt.querySelector('.ch-wl-bs-sort-check');
      if (check) check.style.display = isActive ? '' : 'none';
    });
  }

  function initSort() {
    var desktopSel = document.getElementById('ch-wl-sort-select');
    if (desktopSel && !desktopSel._chInited) {
      desktopSel._chInited = true;
      desktopSel.addEventListener('change', function () {
        applySort(desktopSel.value);
        syncSortUI(desktopSel.value);
      });
    }
    document.querySelectorAll('.ch-wl-bs-sort-opt').forEach(function (opt) {
      if (opt._chInited) return;
      opt._chInited = true;
      opt.addEventListener('click', function () {
        var sortVal = opt.dataset.sort;
        applySort(sortVal);
        syncSortUI(sortVal);
        closeBottomSheet();
      });
    });
  }

  function openBottomSheet() {
    var sheet = document.getElementById('ch-wl-bottom-sheet');
    if (!sheet) return;
    sheet.classList.add('ch-wl-bs-open');
    document.body.style.overflow = 'hidden';
    /* Re-init search inputs inside sheet (they may not have been inited yet) */
    initSearch();
    initSort();
  }

  function closeBottomSheet() {
    var sheet = document.getElementById('ch-wl-bottom-sheet');
    if (!sheet) return;
    sheet.classList.remove('ch-wl-bs-open');
    document.body.style.overflow = '';
  }

  function initBottomSheet() {
    var sheet    = document.getElementById('ch-wl-bottom-sheet');
    var openBtn  = document.getElementById('ch-wl-filter-sort-btn');
    var closeBtn = document.getElementById('ch-wl-bs-close');
    if (!sheet) return;
    var backdrop = sheet.querySelector('.ch-wl-bottom-sheet__backdrop');
    if (openBtn  && !openBtn._chBsInited)  { openBtn._chBsInited  = true; openBtn.addEventListener('click', openBottomSheet); }
    if (closeBtn && !closeBtn._chBsInited) { closeBtn._chBsInited = true; closeBtn.addEventListener('click', closeBottomSheet); }
    if (backdrop && !backdrop._chBsInited) { backdrop._chBsInited = true; backdrop.addEventListener('click', closeBottomSheet); }
  }

  /* ════════════════════════════════════
     WISHLIST PAGE — SELECT MODE
  ════════════════════════════════════ */

  function toggleSelectMode() {
    selectMode = !selectMode;
    selectedItems = {};
    var grid         = document.getElementById('ch-wishlist-items');
    var toggle       = document.getElementById('ch-wl-select-toggle');
    var actionsEl    = document.getElementById('ch-wl-toolbar-actions');
    var filterBtn    = document.getElementById('ch-wl-filter-sort-btn');
    var tabs         = document.getElementById('ch-wl-tabs');
    var center       = document.getElementById('ch-wl-toolbar-center');
    var selectAllBtn = document.getElementById('ch-wl-select-all-btn');

    if (selectMode) {
      if (grid)         grid.classList.add('ch-wl-select-mode');
      if (toggle)       { toggle.classList.add('ch-wl-select-toggle--active'); toggle.setAttribute('aria-pressed', 'true'); }
      if (tabs)         tabs.classList.add('ch-wl-tabs--hidden');
      if (actionsEl)    actionsEl.removeAttribute('hidden');
      if (filterBtn)    filterBtn.setAttribute('hidden', '');
      if (center)       center.setAttribute('hidden', '');
      if (selectAllBtn) selectAllBtn.removeAttribute('hidden');
      if (grid) injectCheckboxes(grid);
    } else {
      if (grid)         grid.classList.remove('ch-wl-select-mode');
      if (toggle)       { toggle.classList.remove('ch-wl-select-toggle--active'); toggle.setAttribute('aria-pressed', 'false'); }
      if (tabs)         tabs.classList.remove('ch-wl-tabs--hidden');
      if (actionsEl)    actionsEl.setAttribute('hidden', '');
      if (filterBtn)    filterBtn.removeAttribute('hidden');
      if (center)       center.removeAttribute('hidden');
      if (selectAllBtn) selectAllBtn.setAttribute('hidden', '');
      if (grid) removeCheckboxes(grid);
    }
    updateGroupActionBtns();
  }

  function injectCheckboxes(grid) {
    grid.querySelectorAll('.card-wrapper').forEach(function (card) {
      if (card.querySelector('.ch-wl-checkbox')) return;
      var cb = document.createElement('button');
      cb.className = 'ch-wl-checkbox';
      cb.type = 'button';
      cb.setAttribute('aria-label', 'Select item');
      cb.setAttribute('aria-pressed', 'false');
      cb.innerHTML =
        '<svg class="ch-wl-checkbox-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<polyline points="20 6 9 17 4 12"/>' +
        '</svg>';
      var wlBtn  = card.querySelector('[data-wishlist-btn]');
      var itemId = wlBtn ? (wlBtn.dataset.variantId || wlBtn.dataset.productId) : '';
      cb.dataset.itemId = itemId || '';
      card.insertAdjacentElement('afterbegin', cb);
      cb.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var id = cb.dataset.itemId;
        if (selectedItems[id]) {
          delete selectedItems[id];
          cb.classList.remove('ch-wl-checkbox--selected');
          card.classList.remove('ch-wl-card--selected');
          cb.setAttribute('aria-pressed', 'false');
        } else {
          selectedItems[id] = true;
          cb.classList.add('ch-wl-checkbox--selected');
          card.classList.add('ch-wl-card--selected');
          cb.setAttribute('aria-pressed', 'true');
        }
        updateGroupActionBtns();
      });
    });
  }

  function removeCheckboxes(grid) {
    grid.querySelectorAll('.ch-wl-checkbox').forEach(function (cb) { cb.remove(); });
    grid.querySelectorAll('.ch-wl-card--selected').forEach(function (card) { card.classList.remove('ch-wl-card--selected'); });
  }

  /* FIX 2: Select All / Deselect All */
  function updateSelectAllBtn() {
    var btn = document.getElementById('ch-wl-select-all-btn');
    if (!btn || !selectMode) return;
    var grid = document.getElementById('ch-wishlist-items');
    if (!grid) return;
    var visible = Array.from(grid.querySelectorAll('.card-wrapper')).filter(function (c) {
      return c.style.display !== 'none';
    });
    if (!visible.length) { btn.textContent = 'Select All'; return; }
    var allSel = visible.every(function (c) { return c.classList.contains('ch-wl-card--selected'); });
    btn.textContent = allSel ? 'Deselect All' : 'Select All';
  }

  function selectAll() {
    var grid = document.getElementById('ch-wishlist-items');
    if (!grid) return;
    var visible = Array.from(grid.querySelectorAll('.card-wrapper')).filter(function (c) {
      return c.style.display !== 'none';
    });
    var allSel = visible.every(function (c) { return c.classList.contains('ch-wl-card--selected'); });
    visible.forEach(function (card) {
      var cb = card.querySelector('.ch-wl-checkbox');
      var id = cb ? cb.dataset.itemId : '';
      if (allSel) {
        delete selectedItems[id];
        card.classList.remove('ch-wl-card--selected');
        if (cb) { cb.classList.remove('ch-wl-checkbox--selected'); cb.setAttribute('aria-pressed', 'false'); }
      } else {
        if (id) selectedItems[id] = true;
        card.classList.add('ch-wl-card--selected');
        if (cb) { cb.classList.add('ch-wl-checkbox--selected'); cb.setAttribute('aria-pressed', 'true'); }
      }
    });
    updateGroupActionBtns();
    updateSelectAllBtn();
  }

  function updateGroupActionBtns() {
    var count   = Object.keys(selectedItems).length;
    var atcBtn  = document.getElementById('ch-wl-atc-selected-btn');
    var moveBtn = document.getElementById('ch-wl-move-btn');
    var isAll   = activeFolder === 'all-my-wishlist';
    if (atcBtn) {
      atcBtn.disabled = count === 0;
      atcBtn.childNodes[atcBtn.childNodes.length - 1].textContent = count > 0 ? ' Add to Cart (' + count + ')' : ' Add to Cart';
    }
    if (moveBtn) {
      moveBtn.disabled = count === 0;
      moveBtn.childNodes[moveBtn.childNodes.length - 1].textContent = isAll ? ' Copy to Folder' : ' Move to Folder';
    }
    updateSelectAllBtn();
  }

  /* ════════════════════════════════════
     WISHLIST PAGE — GROUP ACTIONS
  ════════════════════════════════════ */

  function addSelectedToCart() {
    var ids = Object.keys(selectedItems);
    if (!ids.length) return;
    var grid = document.getElementById('ch-wishlist-items');
    var cartItems = [];
    if (grid) {
      grid.querySelectorAll('.card-wrapper.ch-wl-card--selected').forEach(function (card) {
        var input = card.querySelector('input[name="id"]');
        if (input && input.value) cartItems.push({ id: parseInt(input.value, 10), quantity: 1 });
      });
    }
    if (!cartItems.length) return;
    var btn = document.getElementById('ch-wl-atc-selected-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Adding\u2026'; }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'fetch' },
      body: JSON.stringify({ items: cartItems })
    }).then(function () {
      if (btn) btn.textContent = 'Added!';
      /* FIX 3: open carlahome-cart-drawer and refresh bubble */
      var drawer = document.querySelector('carlahome-cart-drawer');
      if (drawer) {
        if (typeof drawer.refreshDrawer === 'function') {
          drawer.refreshDrawer().then(function () {
            if (typeof drawer.open === 'function') drawer.open(null);
          });
        } else if (typeof drawer.open === 'function') {
          drawer.open(null);
        }
      }
      /* Fallback: update cart bubble via /cart.js */
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var count = cart.item_count || 0;
          document.querySelectorAll('[data-cart-count], .cart-count-bubble span:not(.visually-hidden), #cart-icon-bubble span:not(.visually-hidden)').forEach(function (el) {
            el.textContent = count;
          });
        })
        .catch(function () {});
      setTimeout(function () {
        if (btn) { btn.disabled = false; updateGroupActionBtns(); }
      }, 2500);
    }).catch(function () {
      if (btn) { btn.disabled = false; btn.textContent = 'Error \u2014 try again'; }
    });
  }

  function openMoveFolderPicker() {
    /* Toggle — close if already open */
    var existing = document.getElementById('ch-wl-folder-picker');
    if (existing) { existing.remove(); return; }

    var isAll   = activeFolder === 'all-my-wishlist';
    var folders = getFolders();
    var others  = Object.keys(folders).filter(function (s) { return s !== activeFolder; });

    if (!others.length) {
      var name = window.prompt('No other folders exist yet. Create one to ' + (isAll ? 'copy' : 'move') + ' to:');
      if (name && name.trim()) {
        var newSlug = createFolder(name.trim());
        performMoveOrCopy(newSlug, isAll);
      }
      return;
    }

    var btn = document.getElementById('ch-wl-move-btn');
    var picker = document.createElement('div');
    picker.id = 'ch-wl-folder-picker';
    picker.className = 'ch-wl-folder-picker';
    var label = document.createElement('div');
    label.className = 'ch-wl-folder-picker__label';
    label.textContent = isAll ? 'Copy to folder:' : 'Move to folder:';
    picker.appendChild(label);
    others.forEach(function (slug) {
      var folder = folders[slug];
      var item = document.createElement('button');
      item.className = 'ch-wl-folder-picker__item';
      item.type = 'button';
      item.textContent = folder.name;
      item.addEventListener('click', function () {
        picker.remove();
        performMoveOrCopy(slug, isAll);
      });
      picker.appendChild(item);
    });

    document.body.appendChild(picker);
    if (btn) {
      var rect = btn.getBoundingClientRect();
      picker.style.position = 'fixed';
      picker.style.top  = (rect.bottom + 6) + 'px';
      picker.style.left = rect.left + 'px';
    }

    setTimeout(function () {
      document.addEventListener('click', function closePicker(e) {
        if (!picker.contains(e.target)) {
          picker.remove();
          document.removeEventListener('click', closePicker);
        }
      });
    }, 0);
  }

  function performMoveOrCopy(toSlug, isCopy) {
    var ids = Object.keys(selectedItems);
    ids.forEach(function (id) {
      if (isCopy) copyToFolder(id, toSlug);
      else moveToFolder(id, activeFolder, toSlug);
    });
    selectedItems = {};
    if (!isCopy) {
      selectMode = false;
      renderWishlistPage();
    } else {
      var moveBtn = document.getElementById('ch-wl-move-btn');
      if (moveBtn) {
        var orig = moveBtn.textContent;
        moveBtn.textContent = 'Copied!';
        setTimeout(function () { if (moveBtn) { updateGroupActionBtns(); } }, 2000);
      }
      renderFolderSidebar();
      renderFolderDropdown();
    }
  }

  function initToolbar() {
    var toggle       = document.getElementById('ch-wl-select-toggle');
    var atcBtn       = document.getElementById('ch-wl-atc-selected-btn');
    var moveBtn      = document.getElementById('ch-wl-move-btn');
    var selectAllBtn = document.getElementById('ch-wl-select-all-btn');
    if (toggle       && !toggle._chInited)       { toggle._chInited       = true; toggle.addEventListener('click', toggleSelectMode); }
    if (atcBtn       && !atcBtn._chInited)       { atcBtn._chInited       = true; atcBtn.addEventListener('click', addSelectedToCart); }
    if (moveBtn      && !moveBtn._chInited)      { moveBtn._chInited      = true; moveBtn.addEventListener('click', openMoveFolderPicker); }
    if (selectAllBtn && !selectAllBtn._chInited) { selectAllBtn._chInited = true; selectAllBtn.addEventListener('click', selectAll); }
  }

  function initFolderUI() {
    if (!document.getElementById('ch-wl-sidebar')) return;

    /* Unified delegated click handler */
    document.addEventListener('click', function (e) {
      if (!e.target || typeof e.target.closest !== 'function') return;

      /* Sidebar: select folder */
      var folderBtn = e.target.closest('.ch-wl-folder-btn');
      if (folderBtn && folderBtn.dataset.slug) {
        e.preventDefault();
        activeFolder = folderBtn.dataset.slug;
        renderWishlistPage(); /* re-renders sidebar+dropdown internally */
        closeDrop();
        return;
      }

      /* Mobile dropdown: select folder */
      var dropBtn = e.target.closest('.ch-wl-folder-drop-btn');
      if (dropBtn && dropBtn.dataset.slug) {
        e.preventDefault();
        activeFolder = dropBtn.dataset.slug;
        renderWishlistPage();
        closeDrop();
        return;
      }

      /* Mobile dropdown: toggle */
      if (e.target.closest('#ch-wl-folder-drop-trigger')) {
        var panel   = document.getElementById('ch-wl-folder-drop-panel');
        var trigger = document.getElementById('ch-wl-folder-drop-trigger');
        if (!panel) return;
        var isOpen = !panel.hidden;
        panel.hidden = isOpen;
        if (trigger) trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        return;
      }

      /* Sidebar: rename folder */
      var renameBtn = e.target.closest('.ch-wl-folder-rename-btn');
      if (renameBtn) {
        var rSlug   = renameBtn.dataset.slug;
        var folders = getFolders();
        var curName = folders[rSlug] ? folders[rSlug].name : '';
        var newName = window.prompt('Rename folder:', curName);
        if (newName && newName.trim() && newName.trim() !== curName) {
          renameFolder(rSlug, newName.trim());
          renderFolderSidebar();
          renderFolderDropdown();
        }
        return;
      }

      /* Sidebar: delete folder */
      var deleteBtn = e.target.closest('.ch-wl-folder-delete-btn');
      if (deleteBtn) {
        var dSlug      = deleteBtn.dataset.slug;
        var dFolders   = getFolders();
        var dName      = dFolders[dSlug] ? dFolders[dSlug].name : dSlug;
        if (window.confirm('Delete "' + dName + '"? Items stay in All My Wishlist.')) {
          deleteFolder(dSlug);
          if (activeFolder === dSlug) {
            activeFolder = 'all-my-wishlist';
            renderWishlistPage();
          } else {
            renderFolderSidebar();
            renderFolderDropdown();
          }
        }
        return;
      }

      /* Sidebar: show new folder input */
      if (e.target.closest('#ch-wl-sidebar-add')) {
        var wrap  = document.getElementById('ch-wl-sidebar-new-wrap');
        var input = document.getElementById('ch-wl-sidebar-new-input');
        if (wrap) { wrap.hidden = false; if (input) { input.value = ''; input.focus(); } }
        return;
      }

      /* Sidebar: confirm new folder */
      if (e.target.closest('#ch-wl-sidebar-new-confirm')) {
        submitNewFolder();
        return;
      }

      /* Mobile dropdown: new folder */
      if (e.target.closest('#ch-wl-folder-drop-new-btn')) {
        var dName2 = window.prompt('New folder name:');
        if (dName2 && dName2.trim()) {
          var newSlug2 = createFolder(dName2.trim());
          activeFolder = newSlug2;
          renderWishlistPage();
          closeDrop();
        }
        return;
      }

      /* Close dropdown on outside click */
      var dropWrap = document.getElementById('ch-wl-folder-drop-wrap');
      var dropPanel = document.getElementById('ch-wl-folder-drop-panel');
      if (dropWrap && dropPanel && !dropPanel.hidden && !dropWrap.contains(e.target)) {
        closeDrop();
      }
    });

    /* Sidebar: new folder input keydown */
    var newInput = document.getElementById('ch-wl-sidebar-new-input');
    if (newInput) {
      newInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { submitNewFolder(); }
        else if (e.key === 'Escape') { cancelNewFolder(); }
      });
    }

    /* ATC button from wishlist-card-renderer */
    document.addEventListener('click', function (e) {
      if (!e.target || typeof e.target.closest !== 'function') return;
      var atcBtn = e.target.closest('.ch-wl-atc-btn');
      if (!atcBtn) return;
      var vid = atcBtn.getAttribute('data-variant-id');
      if (!vid) return;
      atcBtn.disabled = true;
      var origHtml = atcBtn.innerHTML;
      atcBtn.textContent = 'Adding\u2026';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'fetch' },
        body: JSON.stringify({ id: parseInt(vid, 10), quantity: 1 })
      }).then(function () {
        atcBtn.textContent = 'Added!';
        setTimeout(function () { atcBtn.innerHTML = origHtml; atcBtn.disabled = false; }, 2000);
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }).catch(function () {
        atcBtn.textContent = 'Error \u2014 try again';
        atcBtn.disabled = false;
      });
    });

    function closeDrop() {
      var panel   = document.getElementById('ch-wl-folder-drop-panel');
      var trigger = document.getElementById('ch-wl-folder-drop-trigger');
      if (panel) panel.hidden = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    function submitNewFolder() {
      var input = document.getElementById('ch-wl-sidebar-new-input');
      var name  = input ? input.value.trim() : '';
      if (!name) return;
      var newSlug = createFolder(name);
      cancelNewFolder();
      activeFolder = newSlug;
      renderWishlistPage();
    }

    function cancelNewFolder() {
      var input = document.getElementById('ch-wl-sidebar-new-input');
      var wrap  = document.getElementById('ch-wl-sidebar-new-wrap');
      if (input) input.value = '';
      if (wrap)  wrap.hidden = true;
    }
  }

  function renderShareBtn() {
    if (document.getElementById('ch-wl-share-btn')) return;
    var header = document.querySelector('.ch-wl-page-header');
    if (!header) return;
    var shareUrl = getShareUrl();
    if (!shareUrl) return;

    var shareBtn = document.createElement('button');
    shareBtn.id   = 'ch-wl-share-btn';
    shareBtn.type = 'button';
    shareBtn.style.cssText = [
      'display:inline-flex', 'align-items:center', 'gap:6px',
      'font-family:var(--ch-font-ui)',
      'font-size:12px', 'font-weight:500', 'color:#2C2825',
      'background:transparent', 'border:1px solid rgba(44,40,37,0.2)',
      'border-radius:4px', 'padding:7px 14px', 'cursor:pointer', 'transition:all 0.18s',
    ].join(';');
    shareBtn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>' +
        '<line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>' +
      '</svg>' +
      'Share wishlist';
    shareBtn.addEventListener('click', function () {
      var url = getShareUrl();
      if (!url) return;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(function () {
          shareBtn.textContent = 'Link copied!';
          setTimeout(function () { shareBtn.textContent = 'Share wishlist'; }, 2000);
        });
      } else {
        window.prompt('Copy this link:', url);
      }
    });
    var metaEl = header.querySelector('.ch-wl-page-meta');
    if (metaEl) {
      metaEl.appendChild(shareBtn);
    } else {
      header.appendChild(shareBtn);
    }
  }

  /* ════════════════════════════════════
     INIT
  ════════════════════════════════════ */

  /* ════════════════════════════════════
     OBSERVER — re-init botones tras AJAX
     product-recommendations carga su contenido
     vía AJAX después de que wishlist.js ya corrió.
     MutationObserver detecta cuando aparece el
     contenido y re-inicializa los botones wishlist.
  ════════════════════════════════════ */

  function observeRecommendations() {
    /* Observar todos los product-recommendations del DOM */
    function attachObserver(rpEl) {
      if (rpEl._chObserved) return;
      rpEl._chObserved = true;
      var observer = new MutationObserver(function(mutations) {
        var hasNewNodes = mutations.some(function(m) { return m.addedNodes.length > 0; });
        if (hasNewNodes) {
          /* Pequeño delay para que el DOM esté estable */
          setTimeout(function() {
            initBtns();
            updateCounter();
          }, 50);
        }
      });
      observer.observe(rpEl, { childList: true, subtree: true });
    }

    /* Observar los que ya existen */
    document.querySelectorAll('product-recommendations').forEach(attachObserver);

    /* Observar los que aparezcan después (secciones AJAX de Shopify) */
    var bodyObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        m.addedNodes.forEach(function(node) {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'PRODUCT-RECOMMENDATIONS') {
            attachObserver(node);
          }
          node.querySelectorAll && node.querySelectorAll('product-recommendations').forEach(attachObserver);
        });
      });
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    migrateFromV1();
    initBtns();
    updateCounter();
    loadSharedWishlist();
    loadPersonalization();
    observeRecommendations();

    if (document.getElementById('ch-wishlist-items')) {
      initCardVariants();
      initFolderUI();
      initTabs();
      initToolbar();
      initSearch();
      initSort();
      initBottomSheet();
      renderWishlistPage();
    }

    if (window.location.pathname === '/search' || window.location.search.includes('q=')) {
      personalizeSearch();
      var searchForm = document.querySelector('form[action="/search"]');
      if (searchForm) {
        searchForm.addEventListener('submit', function () {
          setTimeout(personalizeSearch, 800);
        });
      }
    }

    pdpCrossSell();

    document.addEventListener('shopify:section:load', function () {
      initBtns();
      updateCounter();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* API pública */
  window.CHWishlist = {
    /* Core */
    get:                getWishlist,
    add:                addToWishlist,
    remove:             removeFromWishlist,
    toggle:             toggleWishlist,
    has:                isInWishlist,
    shareUrl:           getShareUrl,
    initBtns:           initBtns,
    /* Folders v2 */
    getFolders:         getFolders,
    getFolder:          getFolder,
    createFolder:       createFolder,
    renameFolder:       renameFolder,
    deleteFolder:       deleteFolder,
    moveToFolder:       moveToFolder,
    copyToFolder:       copyToFolder,
    /* Utilidades */
    getItemsByCategory: getItemsByCategory,
    searchItems:        searchItems,
    sortItems:          sortItems,
    importFromShareUrl: importFromShareUrl,
  };

})();
